import { Validator } from "@serenity-is/corelib";

/**
 * One place for "this form is not valid yet", instead of three.
 *
 * WHAT WAS HAPPENING
 * Submitting an incomplete form announced it in three channels at once: the inline message
 * under every offending field (both handoffs draw that, and theme-core.css releases `.vx` for it),
 * a red **toast** in the corner, and a **tooltip** popped on the first invalid field. Same
 * information, three times — and the toast restyle on 2026-08-17 made the duplication
 * conspicuous, because it moved from a full-width strip behind the top bar to a card that
 * demands attention.
 *
 * The toast is Serenity's, not ours: corelib's `validateOptions().invalidHandler` calls
 * `notifyError(FormValidationTexts.InvalidFormMessage)` — "Please validate empty or invalid
 * inputs (marked with red) before submitting the form.", from the Site text bundle.
 *
 * WHAT THE DESIGN ASKS FOR INSTEAD
 * The interactive prototype's CREATE DIALOG has a `formHasErrors` block: a banner at the top
 * of the form reading "**N fields need attention.**" over a danger-tinted fill. So the
 * form-level message belongs *in the form*, next to the fields it is about — not in the
 * corner of the screen, and not in a tooltip that vanishes.
 *
 * WHAT THIS MODULE DOES
 * Replaces `settings.invalidHandler` on each form's validator instance:
 *   - **drops** the toast and the tooltip (the redundant channels),
 *   - **keeps** the two genuinely useful behaviours from Serenity's handler — expanding a
 *     collapsed category that contains an error, and switching to the tab that holds the
 *     first one, because an error you cannot see is worse than no message at all,
 *   - **adds** the prototype's banner, and keeps it in step as fields are fixed.
 *
 * ⚠ Why not patch `validateOptions` globally: corelib's internal callers close over the
 * local function, not the namespace export, so reassigning `Serenity.validateOptions` would
 * change nothing. `Validator.getInstance(form)` is the supported way in, and its `settings`
 * object is the documented place these handlers live.
 *
 * ⚠ Why the two kept behaviours are reimplemented rather than delegated: the original does
 * all four things in one function, so there is no way to call it minus the toast. They are
 * ten lines of DOM work; if Serenity's own handler grows a fifth behaviour, this is the
 * comment that will explain why it did not appear here.
 */

const PATCHED = "__mValidationPatched";
const BANNER = "m-form-errors";

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
}

/**
 * The label a field is known by, for the summary line.
 *
 * ⚠ The required marker is stripped from ANYWHERE in the string, not just the end. Serenity
 * emits the asterisk as the caption's FIRST child and moves it visually with CSS, so the
 * caption reads "Franchisee Name *" on screen but "*Franchisee Name" in textContent — a
 * trailing-only strip left every name in the banner prefixed with a star.
 */
function labelOf(element: HTMLElement): string {
    const field = element.closest(".field");
    const caption = field?.querySelector(".caption");
    const text = (caption?.textContent || "").replace(/[*]/g, "").replace(/[:：]\s*$/, "").trim();
    return text || element.getAttribute("name") || "";
}

function removeBanner(form: HTMLElement) {
    form.querySelector(":scope > ." + BANNER)?.remove();
}

/**
 * Below this many outstanding errors the banner is suppressed and the inline messages stand
 * alone.
 *
 * ⚠ This exists because the banner and the per-field messages are two channels for the same
 * information, and on a SHORT form that is pure duplication. The sign-in screen made it
 * obvious: two fields, and the banner read "2 fields need attention. Username, Password"
 * directly above those exact two fields, each already showing "This field is required."
 *
 * The banner earns its place when an error can be OUT OF SIGHT — the Franchisee dialog is
 * three columns and ten fields, where the offending one may be below the fold or behind a
 * collapsed category. So the rule is content-driven rather than per-screen: no exceptions list
 * to keep in step, and sign-up (five fields) still gets a summary once three of them fail.
 *
 * Chosen over the alternative of exempting the auth screens by name, which would also have
 * stripped it from sign-up where it is useful, and would have partly reverted the 2026-08-17
 * decision to give the sign-in form a banner at all (before that a toast was its only
 * message — see the header comment).
 */
const BANNER_MIN_ERRORS = 3;

function showBanner(form: HTMLElement, labels: string[]) {
    removeBanner(form);
    if (labels.length < BANNER_MIN_ERRORS) return;

    const banner = el("div", BANNER);
    banner.setAttribute("role", "alert");
    banner.appendChild(el("i", "fa fa-exclamation-circle"));

    const text = el("div", BANNER + "-text");
    text.appendChild(el("strong", undefined,
        `${labels.length} ${labels.length === 1 ? "field needs" : "fields need"} attention.`));
    // The names, so the message is actionable without hunting: a long form can put the
    // offending field below the fold.
    text.appendChild(document.createTextNode(" " + labels.join(", ")));
    banner.appendChild(text);

    form.insertAdjacentElement("afterbegin", banner);
}

/** Errors outstanding at submit time, from the validator's own list. */
function labelsFromValidator(validator: any): string[] {
    const labels: string[] = [];
    for (const item of validator?.errorList || []) {
        const element = (Array.isArray(item.element) ? item.element[0] : item.element) as HTMLElement;
        if (!element) continue;
        const label = labelOf(element);
        if (label && !labels.includes(label)) labels.push(label);
    }
    return labels;
}

/**
 * Errors outstanding right now, read from the fields themselves.
 *
 * ⚠ This cannot use `validator.errorList` and that is the subtle part. `validator.element(el)`
 * — which is what re-checks a field the user just edited — CLEARS `errorList` and refills it
 * with only that one element's result. Reading the list afterwards therefore reports one
 * field, so the banner counted down to "1 field needs attention" while three were still
 * empty. The error CLASS on each control is the state the user is actually looking at, so the
 * live count comes from that instead; `validator.element()` is still called, for its real job
 * of updating the edited field's own class and inline message.
 */
function labelsFromDom(form: HTMLElement): string[] {
    const labels: string[] = [];
    // ⚠ Not `.error` — that class is not a live signal. Serenity puts `errorClass` on the
    // MESSAGE element and, when the field is later fixed, adds `checked` and empties the text
    // but LEAVES `error` in place. Counting `.error` therefore counted every field that had
    // ever failed, so the banner sat at "4 fields need attention" while three were filled in.
    // A message with text in it is the thing the user can actually see.
    form.querySelectorAll<HTMLElement>(".field .vx, .field label.error").forEach(message => {
        if (!message.textContent?.trim()) return;
        const label = labelOf(message);
        if (label && !labels.includes(label)) labels.push(label);
    });
    return labels;
}

function patch(form: HTMLFormElement) {
    if ((form as any)[PATCHED]) return;

    const validator: any = (Validator as any)?.getInstance?.(form);
    if (!validator?.settings) return;

    (form as any)[PATCHED] = true;

    validator.settings.invalidHandler = function (_event: any, v: any) {
        const errors = v?.errorList || [];

        // Kept from Serenity's handler: reveal the error. A collapsed category or an
        // inactive tab hides the field the message is about.
        for (const item of errors) {
            const element = (Array.isArray(item.element) ? item.element[0] : item.element) as HTMLElement;
            element?.closest(".category.collapsed")
                ?.querySelectorAll<HTMLElement>(":scope > .category-title")
                ?.forEach(title => title.click());
        }

        const first = (Array.isArray(errors[0]?.element) ? errors[0].element[0] : errors[0]?.element) as HTMLElement;
        if (first) {
            const paneId = first.closest(".tab-content > .tab-pane[id]:not(.active)")?.getAttribute("id")
                || first.closest(".ui-tabs-panel[id]:not(.ui-tabs-panel-active)")?.getAttribute("id");
            if (paneId)
                document.querySelector<HTMLElement>(`a[href="#${paneId}"]`)?.click();
        }

        // Dropped from Serenity's handler: notifyError(InvalidFormMessage) and the tooltip
        // on the first field. Both say what the banner and the inline messages already say.
        showBanner(form, labelsFromValidator(v));
    };

    // Keep the banner honest while the user works, and drop it once nothing is outstanding.
    //
    // ⚠ `focusout`, not `blur`: blur does not bubble, so a capture listener on the form would
    // never see it, and Serenity's own validator listens for focusout too.
    const refresh = (e?: Event) => {
        if (!form.querySelector("." + BANNER)) return;
        const target = e?.target as HTMLElement;
        // Re-check just the field that changed — this is what clears its inline message and
        // its error class. See labelsFromDom for why the COUNT is not read back from the
        // validator afterwards.
        if (target && typeof validator.element === "function") {
            try { validator.element(target); } catch { /* not a validated element */ }
        }
        setTimeout(() => showBanner(form, labelsFromDom(form)), 0);
    };
    form.addEventListener("change", refresh, true);
    form.addEventListener("focusout", refresh, true);
}

export function installFormValidation() {
    const scan = () => document.querySelectorAll<HTMLFormElement>("form").forEach(patch);

    // Dialogs are added to body after their form is built, and Serenity attaches the
    // validator during construction — so patching on mutation catches every dialog, and the
    // initial scan catches forms already on the page (the sign-in screen, for one).
    const observer = new MutationObserver(() => scan());
    observer.observe(document.body, { childList: true, subtree: true });
    scan();
}
